import { HttpStatusCode } from 'axios';
import { Buffer } from 'buffer';
import { TFunction } from 'i18next';
import jwtDecode from 'jwt-decode';
import { NavigateFunction } from 'react-router-dom';
import {
    apiCall,
    axiosInstanceAuth,
    getLocalRefreshTokenExpiry,
    notifyError,
} from '../../api/axios';
import { AppDispatch, store } from '../../redux/store';
import { ADMIN, DISPATCHER, FINANCE } from '../../utils/constants';
import { IGenericApiResponse } from '../../utils/interfaces';
import { Routes } from '../../utils/routes/constants';
import { API_CONSTANTS } from '../apiConstants';
import {
    ISaveLoginData,
    ISignInResetPasswordUserFormData,
    IUserToken,
} from './interface';
import {
    setPermissionList,
    setSignInData,
    setTenantId,
    setUserEmail,
    setUserRole,
} from './reducer';
import { ILoginUserState } from '../../pages/Login/interfaces';
import { PostHog } from 'posthog-js';
import { dispatchManagerFeatures } from '../../featureFlags/dispatchManagerFeatures';
const { isPosthogOn, isBlankScreenFixEnabled } = dispatchManagerFeatures;

export const getBasicAuthToken = (): string | null => {
    const token: string | null =
        localStorage.getItem('refreshToken') ||
        sessionStorage.getItem('refreshToken') ||
        store.getState().auth.signInData.refreshToken;

    const email: string | null =
        localStorage.getItem('email') || sessionStorage.getItem('email');

    if (!email && !token) {
        return null;
    } else {
        const parameter = email + ':' + token;
        const encoded = Buffer.from(parameter)?.toString('base64');
        return encoded;
    }
};

export const clearLocalData = (): void => {
    localStorage.removeItem('userId');
    localStorage.removeItem('email');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('refreshTokenExpiry');
    localStorage.removeItem('userRole');
    localStorage.removeItem('token');
    localStorage.removeItem('actionPermissions');
    localStorage.removeItem('tenantId');
    localStorage.removeItem('daysLeft');
    localStorage.removeItem('paymentLink');
    localStorage.removeItem('showPaymentLink');
    localStorage.removeItem('freeTrialModalShown');
    localStorage.removeItem('hasSeenBrowserWarning');
    sessionStorage.removeItem('userId');
    sessionStorage.removeItem('email');
    sessionStorage.removeItem('refreshToken');
    sessionStorage.removeItem('refreshTokenExpiry');
    sessionStorage.removeItem('userRole');
    sessionStorage.removeItem('token');
    sessionStorage.removeItem('tenantId');
    sessionStorage.removeItem('actionPermissions');
    sessionStorage.removeItem('daysLeft');
    sessionStorage.removeItem('paymentLink');
    sessionStorage.removeItem('showPaymentLink');
    sessionStorage.removeItem('freeTrialModalShown');
};

const SESSION_EXPIRED_NOTICE = 'sessionExpiredNotice';

// For sessions the app ends itself, not ones the user logs out of: leaves a
// note so the login page can say why they were sent there.
const endExpiredSession = (): void => {
    clearLocalData();
    sessionStorage.setItem(SESSION_EXPIRED_NOTICE, 'true');
};

export const consumeSessionExpiredNotice = (): boolean => {
    const hasNotice = sessionStorage.getItem(SESSION_EXPIRED_NOTICE) !== null;
    sessionStorage.removeItem(SESSION_EXPIRED_NOTICE);
    return hasNotice;
};

export const hasStoredSession = (): boolean =>
    !!(sessionStorage.getItem('token') || localStorage.getItem('token'));

// A stored session whose refresh token has expired can never be renewed, so
// drop it and start at the login page instead of on pages whose every request
// will be rejected.
export const clearExpiredSession = (): void => {
    const refreshTokenExpiry = getLocalRefreshTokenExpiry();
    if (refreshTokenExpiry && new Date(refreshTokenExpiry) <= new Date()) {
        endExpiredSession();
    }
};

export const signInUser =
    (
        user: ILoginUserState,
        navigate: NavigateFunction,
        t: TFunction,
        posthog?: PostHog,
    ) =>
    async (dispatch: AppDispatch) => {
        const { email, password, rememberMe } = user;
        const parameter = email + ':' + password;
        const encoded = Buffer.from(parameter)?.toString('base64');
        const response: IGenericApiResponse<IUserToken> = await apiCall(
            'get',
            `${API_CONSTANTS.auth}`,
            {
                headers: {
                    Authorization: 'Basic ' + encoded,
                },
            },
            true,
        );

        if (!response || response.status !== HttpStatusCode.Ok) {
            return;
        }

        const loginDetail = response.data;
        // The decoded token is an object of claims; the user id is its sub.
        const userId = isBlankScreenFixEnabled
            ? loginDetail.token &&
              jwtDecode<{ sub?: string }>(loginDetail.token).sub
            : loginDetail.token && jwtDecode<string>(loginDetail.token);
        clearLocalData();
        const permissionList = Object.values(loginDetail.actionPermissions);

        if (
            !(
                loginDetail.roles.includes(ADMIN) ||
                loginDetail.roles.includes(DISPATCHER) ||
                loginDetail.roles.includes(FINANCE)
            )
        ) {
            notifyError(t('Common:unauthorized'));
            return;
        }

        if (loginDetail.refreshToken && loginDetail.refreshTokenExpiry) {
            saveLoginData({
                local: rememberMe,
                refreshToken: loginDetail.refreshToken,
                refreshTokenExpiry: loginDetail.refreshTokenExpiry?.toString(),
                token: loginDetail.token,
                userRole: loginDetail.roles,
                userId: userId,
                email: email,
                tenantId: loginDetail.tenantId,
                actionPermissions: loginDetail.actionPermissions,
                daysLeft: loginDetail.daysLeft,
                paymentLink: loginDetail.paymentLink,
                showPaymentLink: loginDetail.showPaymentLink,
            });
        }

        dispatch(setSignInData(loginDetail));
        dispatch(setTenantId(loginDetail.tenantId));
        dispatch(setUserRole(loginDetail.roles[0]));
        dispatch(setUserEmail(email));
        dispatch(setPermissionList(permissionList));

        if (isPosthogOn) {
            posthog?.identify(email);
        }

        navigate(Routes.dashboard);
    };

export const canSignInResetPasswordUser =
    (formData: ISignInResetPasswordUserFormData) =>
    async (dispatch: AppDispatch): Promise<boolean> => {
        const { email, resetCode } = formData;
        const parameter = email + ':' + resetCode;
        const encoded = Buffer?.from(parameter)?.toString('base64');
        const response: IGenericApiResponse<IUserToken> = await apiCall(
            'get',
            `${API_CONSTANTS.auth}${API_CONSTANTS.transient}`,
            {
                headers: {
                    Authorization: 'Basic ' + encoded,
                },
            },
            true,
        );

        if (
            response.status &&
            response.status === HttpStatusCode.Unauthorized
        ) {
            return false;
        } else {
            dispatch(setSignInData(response.data));
            return true;
        }
    };

export const getRefreshToken = async (): Promise<void> => {
    try {
        const res: IGenericApiResponse<IUserToken> = await axiosInstanceAuth[
            'get'
        ](`${API_CONSTANTS.auth}${API_CONSTANTS.refresh}`, {});

        if (res.data.refreshToken && res.data.refreshTokenExpiry) {
            const isRememberMe = !!localStorage.getItem('refreshToken');
            saveLoginData({
                local: isRememberMe,
                refreshToken: res.data.refreshToken,
                refreshTokenExpiry: res.data.refreshTokenExpiry,
                token: res.data.token,
                userRole: res.data.roles,
                tenantId: res.data.tenantId,
                actionPermissions: res.data.actionPermissions,
                daysLeft: res.data.daysLeft,
                paymentLink: res.data.paymentLink,
                showPaymentLink: res.data.showPaymentLink,
            });
        }

        window.location.reload();
    } catch (error) {
        console.error(error);
        if (isBlankScreenFixEnabled) {
            endExpiredSession();
        } else {
            clearLocalData();
        }
        window.location.replace(window.location.origin + Routes.login);
    }
};

export const rememberMeLogin = async (
    navigate: NavigateFunction,
): Promise<void> => {
    try {
        const res: IGenericApiResponse<IUserToken> = await axiosInstanceAuth[
            'get'
        ](`${API_CONSTANTS.auth}${API_CONSTANTS.refresh}`, {});

        if (res.status === HttpStatusCode.Ok) {
            const isRememberMe = !!localStorage.getItem('refreshToken');
            if (isRememberMe) {
                saveLoginData({
                    local: isRememberMe,
                    refreshToken: res.data.refreshToken,
                    refreshTokenExpiry: res.data.refreshTokenExpiry,
                    token: res.data.token,
                    userRole: res.data.roles,
                    tenantId: res.data.tenantId,
                    actionPermissions: res.data.actionPermissions,
                });
            }

            navigate(Routes.dashboard);
        }
    } catch (error) {
        console.error(error);
    }
};

const saveLoginData = ({
    local,
    refreshToken,
    refreshTokenExpiry,
    token,
    userRole,
    userId,
    email,
    tenantId,
    actionPermissions,
    daysLeft,
    paymentLink,
    showPaymentLink,
}: ISaveLoginData): void => {
    const storageType: Storage = local ? localStorage : sessionStorage;
    userId && storageType.setItem('userId', userId);
    email && storageType.setItem('email', email);
    storageType.setItem('refreshToken', refreshToken);
    storageType.setItem('refreshTokenExpiry', refreshTokenExpiry);
    userRole && storageType.setItem('userRole', JSON.stringify(userRole));
    storageType.setItem('token', token);
    tenantId && storageType.setItem('tenantId', tenantId);
    actionPermissions &&
        storageType.setItem(
            'actionPermissions',
            JSON.stringify(actionPermissions),
        );
    daysLeft !== undefined &&
        daysLeft !== null &&
        storageType.setItem('daysLeft', daysLeft.toString());
    paymentLink && storageType.setItem('paymentLink', paymentLink);
    showPaymentLink &&
        storageType.setItem('showPaymentLink', JSON.stringify(showPaymentLink));
};

export const isUserLoggedIn = (): boolean => {
    return getBasicAuthToken() !== null;
};

export const signOutUser = (
    navigate?: NavigateFunction | undefined,
    posthog?: PostHog,
): void => {
    if (isPosthogOn) {
        posthog?.reset();
    }
    if (navigate) {
        clearLocalData();
        navigate(Routes.login);
        window.location.reload();
    } else if (isBlankScreenFixEnabled) {
        endExpiredSession();
        window.location.replace(window.location.origin + Routes.login);
    }
};
